import { addPresetTemplates, getPresetTemplates, listTemplates } from 'src/api/functions/templateApi';

const PAGE_SIZE = 100; // the server's maximum
const MAX_PAGES = 20; // a runaway guard, far above any real account

/** Every template name the user has, lower-cased — or null if any page fails. */
async function existingTemplateNames(accessToken: string): Promise<Set<string> | null> {
  const names = new Set<string>();
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result = await listTemplates(accessToken, { limit: PAGE_SIZE, offset: page * PAGE_SIZE });
    if ('error' in result) return null;
    const templates = result.data?.templates ?? [];
    templates.forEach((t) => names.add(t.name.trim().toLowerCase()));
    // A short page is the last one; `total` saves a request when it lands
    // exactly on a page boundary.
    if (templates.length < PAGE_SIZE) break;
    const total = result.data?.total;
    if (total !== undefined && (page + 1) * PAGE_SIZE >= total) break;
  }
  return names;
}

/**
 * Give the user every share-template preset they don't have yet (#958).
 *
 * Onboarding used to ask which presets to add and pre-select two of four;
 * the answer is "all of them", so nobody is asked any more. Templates are per
 * user, and POST /sharetemplates/presets is not idempotent (each call creates
 * fresh copies), so presets already present BY NAME are skipped — a reloaded
 * wizard must not double them. The list is paged, so every page is read. If
 * any of it can't be read, nothing is added: a missing template is fixable
 * from Shares, duplicates are clutter.
 *
 * Resolves to the number of templates created; never rejects.
 */
export async function provisionSharePresets(accessToken: string): Promise<number> {
  try {
    const [presetsResult, existingNames] = await Promise.all([
      getPresetTemplates(accessToken),
      existingTemplateNames(accessToken),
    ]);
    if ('error' in presetsResult || !existingNames) return 0;

    const missing = (presetsResult.data?.presets ?? [])
      .filter((p) => !existingNames.has(p.name.trim().toLowerCase()))
      .map((p) => p.preset_id);
    if (missing.length === 0) return 0;

    const added = await addPresetTemplates(accessToken, missing);
    return 'error' in added ? 0 : added.data?.created ?? 0;
  } catch {
    return 0;
  }
}
